import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, updateMyProfile, updateProfileTheme, markNotificationsRead, createPortalSession, deleteAccount, resendVerification, getReferralInfo, applyReferral, getCoachTwinProgress } from '../services/api';
import LinkedPlatforms from './LinkedPlatforms';
import ArchetypeBadge from './ArchetypeBadge';

const GAMES = [
  '', 'Valorant', 'CS2', 'League of Legends', 'Dota 2', 'Overwatch 2',
  'Apex Legends', 'Rocket League', 'Fortnite', 'Rainbow Six Siege',
  'Call of Duty', 'Teamfight Tactics', 'Other',
];
const ROLES = ['', 'Entry Fragger', 'Support', 'IGL', 'Lurker', 'AWPer / Sniper', 'Flex', 'Jungler', 'Tank', 'Carry', 'Healer'];
const REGIONS = ['', 'NA', 'EU', 'APAC', 'LATAM', 'ME', 'Other'];

// ── Section wrapper ────────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{ marginBottom: 20 }}
    >
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {children}
    </motion.div>
  );
}

// ── Claimed profile card ───────────────────────────────────────────────────
function ClaimedProfileCard({ shareableId }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProfile(shareableId)
      .then(setProfile)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shareableId]);

  if (loading) {
    return (
      <div style={{
        padding: '16px 20px', borderRadius: 10, border: '1px solid var(--border)',
        background: 'var(--bg-secondary)', opacity: 0.5,
      }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading...</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{
        padding: '16px 20px', borderRadius: 10, border: '1px solid var(--border)',
        background: 'var(--bg-secondary)',
      }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{shareableId}</span>
        <p style={{ fontSize: 12, color: 'var(--danger)', margin: '4px 0 0' }}>Profile unavailable</p>
      </div>
    );
  }

  const overallScore = profile.scores?.overallScore ??
    Math.round(Object.values(profile.scores || {}).filter((_, i) =>
      Object.keys(profile.scores || {})[i] !== 'overallScore'
    ).reduce((a, b) => a + b, 0) / Math.max(Object.keys(profile.scores || {}).filter(k => k !== 'overallScore').length, 1));

  const genreColor = { general: '#00d4ff', fps: '#ef4444', moba: '#a855f7' }[profile.genre] || '#00d4ff';

  return (
    <Link
      to={`/profile/${shareableId}`}
      style={{ textDecoration: 'none' }}
    >
      <div style={{
        padding: '16px 20px', borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'var(--bg-secondary)',
        display: 'flex', alignItems: 'center', gap: 16,
        transition: 'border-color 0.15s, background 0.15s',
        cursor: 'pointer',
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-secondary)'; }}
      >
        {/* Archetype badge */}
        <div style={{ flexShrink: 0 }}>
          <ArchetypeBadge archetypeId={profile.archetype?.id} archetypeName={profile.archetype?.name} size="sm" />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{profile.playerTag}</span>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 1.5, padding: '2px 8px',
              borderRadius: 20, border: `1px solid ${genreColor}55`, color: genreColor,
              textTransform: 'uppercase',
            }}>
              {profile.genre}
            </span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{shareableId}</span>
        </div>

        {/* Score */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--accent)', lineHeight: 1 }}>{overallScore}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Overall</div>
        </div>

        <span style={{ color: 'var(--text-muted)', fontSize: 16, flexShrink: 0 }}>→</span>
      </div>
    </Link>
  );
}

// ── Settings form ──────────────────────────────────────────────────────────
function SettingsForm({ user, refreshUser }) {
  const [bio, setBio] = useState(user?.bio || '');
  const [game, setGame] = useState(user?.game || '');
  const [rank, setRank] = useState(user?.rank || '');
  const [age, setAge] = useState(user?.age || '');
  const [role, setRole] = useState(user?.role || '');
  const [region, setRegion] = useState(user?.region || '');
  const [opp, setOpp] = useState(user?.openToOpportunities || false);
  const [twitter, setTwitter] = useState(user?.socialLinks?.twitter || '');
  const [twitch, setTwitch] = useState(user?.socialLinks?.twitch || '');
  const [youtube, setYoutube] = useState(user?.socialLinks?.youtube || '');
  const [discord, setDiscord] = useState(user?.socialLinks?.discord || '');
  const [website, setWebsite] = useState(user?.socialLinks?.website || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const socialLinks = { twitter, twitch, youtube, discord, website };
      await updateMyProfile({ bio, game, rank, age: age || null, role, region, openToOpportunities: opp, socialLinks });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' };
  const labelStyle = { fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 0.8, display: 'block', marginBottom: 6 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Bio */}
      <div>
        <label style={labelStyle}>
          BIO <span style={{ fontWeight: 400, letterSpacing: 0 }}>({bio.length}/200)</span>
        </label>
        <textarea
          className="input"
          value={bio}
          onChange={e => setBio(e.target.value.slice(0, 200))}
          placeholder="Tell scouts about your playstyle, experience, and goals..."
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
        />
      </div>

      {/* Game + Rank */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 160 }}>
          <label style={labelStyle}>PRIMARY GAME</label>
          <select className="input" value={game} onChange={e => setGame(e.target.value)} style={inputStyle}>
            {GAMES.map(g => <option key={g} value={g}>{g || 'Not specified'}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={labelStyle}>CURRENT RANK</label>
          <input
            className="input"
            type="text"
            value={rank}
            onChange={e => setRank(e.target.value.slice(0, 40))}
            placeholder="e.g. Diamond 2"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Role + Region + Age */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={labelStyle}>ROLE</label>
          <select className="input" value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
            {ROLES.map(r => <option key={r} value={r}>{r || 'Not specified'}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={labelStyle}>REGION</label>
          <select className="input" value={region} onChange={e => setRegion(e.target.value)} style={inputStyle}>
            {REGIONS.map(r => <option key={r} value={r}>{r || 'Not specified'}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 100 }}>
          <label style={labelStyle}>AGE</label>
          <input
            className="input"
            type="number"
            min={13}
            max={99}
            value={age}
            onChange={e => setAge(e.target.value)}
            placeholder="—"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Open to opportunities */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', borderRadius: 10,
        background: opp ? 'rgba(0,212,255,0.05)' : 'var(--bg-secondary)',
        border: `1px solid ${opp ? 'var(--accent)' : 'var(--border)'}`,
        transition: 'all 0.2s',
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>Open to Opportunities</div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '3px 0 0' }}>
            Makes you discoverable by scouts on the dashboard
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpp(v => !v)}
          style={{
            width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
            background: opp ? 'var(--accent)' : 'var(--border)',
            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
          }}
        >
          <span style={{
            position: 'absolute', top: 4, left: opp ? 24 : 3,
            width: 18, height: 18, borderRadius: '50%',
            background: '#fff', transition: 'left 0.2s',
          }} />
        </button>
      </div>

      {/* Social / Contact links */}
      <div style={{ paddingTop: 4 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 0.8, marginBottom: 14 }}>
          CONTACT & SOCIAL LINKS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#1d9bf0', fontWeight: 700, minWidth: 72 }}>Twitter / X</span>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14 }}>@</span>
              <input
                className="input"
                type="text"
                value={twitter}
                onChange={e => setTwitter(e.target.value.replace(/^@/, '').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 50))}
                placeholder="yourhandle"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%', paddingLeft: 28 }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#9146ff', fontWeight: 700, minWidth: 72 }}>Twitch</span>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 11 }}>twitch.tv/</span>
              <input
                className="input"
                type="text"
                value={twitch}
                onChange={e => setTwitch(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 50))}
                placeholder="channel"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%', paddingLeft: 76 }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#ff0000', fontWeight: 700, minWidth: 72 }}>YouTube</span>
            <input
              className="input"
              type="url"
              value={youtube}
              onChange={e => setYoutube(e.target.value.slice(0, 200))}
              placeholder="https://youtube.com/@yourchannel"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', flex: 1 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#5865f2', fontWeight: 700, minWidth: 72 }}>Discord</span>
            <input
              className="input"
              type="text"
              value={discord}
              onChange={e => setDiscord(e.target.value.slice(0, 50))}
              placeholder="username or username#1234"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', flex: 1 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 700, minWidth: 72 }}>Website</span>
            <input
              className="input"
              type="url"
              value={website}
              onChange={e => setWebsite(e.target.value.slice(0, 200))}
              placeholder="https://yoursite.com"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', flex: 1 }}
            />
          </div>

        </div>
      </div>

      {error && <p style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{error}</p>}

      <div>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving}
          style={{ minWidth: 140 }}
        >
          {saved ? '✓ Changes Saved' : saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ── Profile Theme Section ──────────────────────────────────────────────────
const ACCENT_COLORS = [
  { label: 'Neon Cyan',   value: '#00d4ff' },
  { label: 'Toxic Green', value: '#10b981' },
  { label: 'Violet',      value: '#8b5cf6' },
  { label: 'Ember Red',   value: '#ef4444' },
  { label: 'Gold',        value: '#f59e0b' },
  { label: 'Arctic',      value: '#38bdf8' },
];
const CARD_STYLES = [
  { label: 'Default',  value: 'default',  desc: 'Archetype colors' },
  { label: 'Glass',    value: 'glass',    desc: 'Frosted & translucent' },
  { label: 'Minimal',  value: 'minimal',  desc: 'Flat & clean' },
  { label: 'Neon',     value: 'neon',     desc: 'High contrast glow' },
];

function ThemeSection({ user, refreshUser }) {
  const isPlayerPro = user?.subscription?.plan === 'player_pro' && user?.subscription?.status === 'active';
  const [accentColor, setAccentColor] = useState(user?.profileTheme?.accentColor || null);
  const [cardStyle, setCardStyle] = useState(user?.profileTheme?.cardStyle || 'default');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfileTheme({ accentColor, cardStyle });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 0.8, display: 'block', marginBottom: 10 };

  if (!isPlayerPro) {
    return (
      <div style={{ padding: '16px 20px', background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 18 }}>🔒</span>
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Premium Profile Theme</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
            <Link to="/pricing" style={{ color: '#f59e0b' }}>Upgrade to Player Pro</Link> to unlock accent colors &amp; card styles.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Card Style */}
      <div>
        <label style={labelStyle}>CARD STYLE</label>
        <div className="theme-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {CARD_STYLES.map(s => (
            <button
              key={s.value}
              onClick={() => setCardStyle(s.value)}
              style={{
                padding: '10px 8px', borderRadius: 8, border: `2px solid ${cardStyle === s.value ? '#f59e0b' : 'var(--border)'}`,
                background: cardStyle === s.value ? '#f59e0b18' : 'var(--bg-secondary)',
                color: cardStyle === s.value ? '#f59e0b' : 'var(--text-muted)',
                cursor: 'pointer', textAlign: 'center',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13 }}>{s.label}</div>
              <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Accent Color */}
      <div>
        <label style={labelStyle}>ACCENT COLOR <span style={{ fontWeight: 400, letterSpacing: 0 }}>(overrides archetype default)</span></label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Reset to archetype default */}
          <button
            onClick={() => setAccentColor(null)}
            title="Use archetype default"
            style={{
              width: 32, height: 32, borderRadius: '50%', border: `2px solid ${accentColor === null ? '#fff' : 'var(--border)'}`,
              background: 'var(--bg-secondary)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: accentColor === null ? '#fff' : 'var(--text-muted)',
            }}
          >✦</button>
          {ACCENT_COLORS.map(c => (
            <button
              key={c.value}
              onClick={() => setAccentColor(c.value)}
              title={c.label}
              style={{
                width: 32, height: 32, borderRadius: '50%', border: `2px solid ${accentColor === c.value ? '#fff' : 'transparent'}`,
                background: c.value, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: accentColor === c.value ? `0 0 8px ${c.value}` : 'none',
              }}
            >
              {accentColor === c.value && <span style={{ color: '#000', fontSize: 14, fontWeight: 900 }}>✓</span>}
            </button>
          ))}
        </div>
      </div>

      <div>
        <button className="btn btn--primary" onClick={handleSave} disabled={saving} style={{ minWidth: 140, background: '#f59e0b', borderColor: '#f59e0b', color: '#000' }}>
          {saved ? '✓ Theme Saved' : saving ? 'Saving...' : 'Save Theme'}
        </button>
      </div>
    </div>
  );
}

// ── Notification item ──────────────────────────────────────────────────────
function NotificationItem({ notification }) {
  const timeAgo = (ts) => {
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (d > 0) return `${d}d ago`;
    if (h > 0) return `${h}h ago`;
    if (m > 0) return `${m}m ago`;
    return 'just now';
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 0',
      borderBottom: '1px solid var(--border)',
      opacity: notification.read ? 0.55 : 1,
    }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>★</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
          A scout bookmarked your profile{' '}
          <Link
            to={`/profile/${notification.shareableId}`}
            style={{ color: 'var(--accent)', fontWeight: 700 }}
          >
            {notification.shareableId}
          </Link>
        </span>
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
        {timeAgo(notification.at)}
      </span>
      {!notification.read && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0,
        }} />
      )}
    </div>
  );
}

// ── Subscription Section ───────────────────────────────────────────────────
const PLAN_LABELS = {
  free: { label: 'Free', color: 'var(--text-muted)' },
  scout: { label: 'Scout Pro', color: '#00d4ff' },
  team: { label: 'Team Plan', color: '#a855f7' },
};

function SubscriptionSection({ user }) {
  const [loadingPortal, setLoadingPortal] = useState(false);

  const sub = user?.subscription || { plan: 'free', status: null };
  const credits = user?.retestCredits || 0;
  const featuredProfiles = (user?.featuredProfiles || []).filter((fp) => fp.featuredUntil > Date.now());
  const planMeta = PLAN_LABELS[sub.plan] || PLAN_LABELS.free;

  const openPortal = async () => {
    setLoadingPortal(true);
    try {
      const data = await createPortalSession();
      window.location.href = data.url;
    } catch {
      setLoadingPortal(false);
    }
  };

  return (
    <Section title="Subscription" subtitle="Your plan, credits, and active profile boosts.">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: credits > 0 || featuredProfiles.length > 0 ? 20 : 0 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Plan</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: planMeta.color }}>{planMeta.label}</span>
            {sub.currentPeriodEnd && sub.plan !== 'free' && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                · Renews {new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {sub.plan !== 'free' ? (
            <button className="btn btn--ghost" style={{ fontSize: 12 }} onClick={openPortal} disabled={loadingPortal}>
              {loadingPortal ? 'Opening…' : 'Manage Billing'}
            </button>
          ) : (
            <Link to="/pricing" className="btn btn--primary" style={{ fontSize: 12 }}>
              Upgrade Plan
            </Link>
          )}
        </div>
      </div>

      {(credits > 0 || featuredProfiles.length > 0) && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {credits > 0 && (
            <div style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #10b98144', background: 'rgba(16,185,129,0.06)', flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 11, color: '#10b981', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Re-test Credits</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#10b981' }}>{credits}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>skips available</div>
            </div>
          )}
          {featuredProfiles.map((fp) => {
            const daysLeft = Math.ceil((fp.featuredUntil - Date.now()) / 86400000);
            return (
              <div key={fp.shareableId} style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #f59e0b44', background: 'rgba(245,158,11,0.06)', flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, color: '#f59e0b', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>★ Featured</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  <a href={`/profile/${fp.shareableId}`} style={{ color: 'inherit', textDecoration: 'none' }}>{fp.shareableId}</a>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{daysLeft}d remaining</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: credits > 0 || featuredProfiles.length > 0 ? 14 : 0, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Link to="/billing" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>View billing details →</Link>
      </div>
    </Section>
  );
}

// ── Coach Twin progress widget ─────────────────────────────────────────────
const PERSONA_LABELS = {
  'steady-rock-captain':       'The Steady Captain',
  'poker-pro':                 'The Poker Pro',
  'ufc-corner-coach':          'The Corner Coach',
  'chess-grandmaster-mentor':  'The Chess Grandmaster',
  'olympic-coach':             'The Olympic Coach',
  'systems-engineer':          'The Systems Engineer',
  'marksmanship-instructor':   'The Marksmanship Instructor',
  'parkour-coach':             'The Parkour Coach',
  'old-school-boxing-coach':   'The Boxing Coach',
  'ninja-sensei':              'The Sensei',
  'f1-race-engineer':          'The Race Engineer',
  'orchestra-maestro':         'The Maestro',
  'big-game-hunter':           'The Hunter',
  'philosophical-chess-coach': 'The Philosopher',
  'nfl-playcaller':            'The Playcaller',
  'experienced-warm-coach':    'Your Coach',
};

function CoachTwinSection({ user }) {
  const [progress, setProgress] = useState(undefined);

  useEffect(() => {
    getCoachTwinProgress()
      .then(setProgress)
      .catch(() => setProgress(null));
  }, []);

  if (progress === undefined) return null; // still loading
  if (!progress) return null;               // not enrolled — hide section entirely

  const { day, totalDays, status, paid, archetype, coachPersona, weakestDimension, completedAt, certificateUrl } = progress;
  const pct = Math.max(0, Math.min(100, Math.round((day / totalDays) * 100)));
  const personaLabel = PERSONA_LABELS[coachPersona] || 'Your Coach';

  const freeGated = !paid && (status === 'paused_awaiting_upgrade' || day >= 3);
  const isComplete = status === 'completed';

  // Next-message countdown: next 19:00 London. Rough approx in user's local time.
  const now = new Date();
  const next = new Date();
  next.setHours(19, 0, 0, 0);
  if (now.getTime() > next.getTime()) next.setDate(next.getDate() + 1);
  const hoursToNext = Math.round((next.getTime() - now.getTime()) / 3600000);

  return (
    <Section
      title="Coach Twin"
      subtitle={`Your 30-day personalized coaching program. ${isComplete ? 'Completed.' : `Day ${day} of ${totalDays}.`}`}
    >
      <div style={{
        padding: '18px 20px',
        borderRadius: 12,
        border: '1px solid #00d4ff33',
        background: 'linear-gradient(135deg, rgba(0,212,255,0.06), rgba(168,85,247,0.04))',
        marginBottom: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, color: '#00d4ff', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>
              {personaLabel}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {archetype} · Focus: <span style={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'capitalize' }}>{weakestDimension}</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#00d4ff', letterSpacing: '-0.02em' }}>
              {day}<span style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: 15 }}>/{totalDays}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {isComplete ? 'complete' : `next in ~${hoursToNext}h`}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden', margin: '8px 0 4px' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#00d4ff,#a855f7)', transition: 'width 0.4s' }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>{pct}%</div>
      </div>

      {/* Paywall CTA */}
      {freeGated && !isComplete && (
        <div style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid #f59e0b55',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b', marginBottom: 2 }}>
              Unlock days {Math.max(day + 1, 4)}–30
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Full 30-day program + completion certificate with Player Pro.
            </div>
          </div>
          <Link to="/pricing" className="btn btn--primary" style={{ fontSize: 12, padding: '8px 18px', background: '#f59e0b', borderColor: '#f59e0b', color: '#000' }}>
            Upgrade to Player Pro
          </Link>
        </div>
      )}

      {/* Certificate */}
      {isComplete && certificateUrl && (
        <div style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(16,185,129,0.08)',
          border: '1px solid #10b98155',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981', marginBottom: 2 }}>
              Certificate earned {completedAt ? `· ${new Date(completedAt).toLocaleDateString()}` : ''}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              30 days complete. Share it.
            </div>
          </div>
          <a href={certificateUrl} target="_blank" rel="noreferrer" className="btn btn--primary" style={{ fontSize: 12, padding: '8px 18px', background: '#10b981', borderColor: '#10b981', color: '#000' }}>
            View Certificate
          </a>
        </div>
      )}

      {/* Active paid state */}
      {paid && !isComplete && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
          Daily messages arrive at 19:00 London time. Check your inbox.
        </div>
      )}
    </Section>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function MyProfile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [verifyResent, setVerifyResent] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifySuccess, setVerifySuccess] = useState(false);

  useEffect(() => {
    document.title = 'My Profile | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);
  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  // Mark all notifications as read when the page loads
  useEffect(() => {
    if (!user) return;
    const unread = user.notifications?.some((n) => !n.read);
    if (unread) {
      markNotificationsRead().then(() => refreshUser()).catch(() => {});
    }
  }, []); // run once on mount

  // Apply pending referral code from localStorage
  useEffect(() => {
    const pendingRef = localStorage.getItem('nr_ref');
    if (pendingRef && user) {
      localStorage.removeItem('nr_ref');
      applyReferral(pendingRef).catch(() => {});
    }
  }, [user]);

  // Detect email_verified redirect from server
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('email_verified') === '1') {
      setVerifySuccess(true);
      refreshUser().catch(() => {});
      window.history.replaceState({}, '', '/my-profile');
    } else if (params.get('verify_error')) {
      setVerifyError(decodeURIComponent(params.get('verify_error')));
      window.history.replaceState({}, '', '/my-profile');
    }
  }, []); // run once on mount

  const handleResendVerification = async () => {
    setVerifyError('');
    try {
      await resendVerification();
      setVerifyResent(true);
    } catch (err) {
      setVerifyError(err.response?.data?.error || 'Failed to resend. Try again later.');
    }
  };

  if (!user) return null;

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : null;

  const maskedEmail = user.email
    ? user.email.replace(/(.{2}).*(@.*)/, '$1***$2')
    : '';

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Page header */}
      <div style={{
        borderBottom: '1px solid var(--border)',
        padding: '32px 0 28px',
        marginBottom: 32,
        background: 'linear-gradient(180deg, rgba(0,212,255,0.04) 0%, transparent 100%)',
      }}>
        <div className="container" style={{ maxWidth: 720 }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <p style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
              My Account
            </p>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 900, color: 'var(--text-primary)', margin: 0, letterSpacing: -0.5 }}>
                  {user.playerTag}
                </h1>
                <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{maskedEmail}</span>
                  {memberSince && (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Member since {memberSince}</span>
                  )}
                  {user.claimedProfiles?.length > 0 && (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {user.claimedProfiles.length} claimed profile{user.claimedProfiles.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={logout}
                className="btn btn--ghost"
                style={{ fontSize: 13, padding: '7px 18px' }}
              >
                Sign Out
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container" style={{ maxWidth: 720 }}>

        {/* Email verification banner */}
        {verifySuccess && (
          <div style={{
            background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981',
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            fontSize: 13, color: '#10b981', fontWeight: 600,
          }}>
            Email verified successfully. Your account is fully confirmed.
          </div>
        )}
        {verifyError && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            fontSize: 13, color: '#ef4444',
          }}>
            {verifyError}
          </div>
        )}
        {!user.emailVerified && !verifySuccess && (
          <div style={{
            background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b',
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 13, color: '#f59e0b' }}>
              Please verify your email address to unlock all features.
            </span>
            {verifyResent ? (
              <span style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>Sent! Check your inbox.</span>
            ) : (
              <button
                className="btn btn--ghost"
                style={{ fontSize: 12, padding: '5px 14px', borderColor: '#f59e0b', color: '#f59e0b', flexShrink: 0 }}
                onClick={handleResendVerification}
              >
                Resend email
              </button>
            )}
          </div>
        )}

        {/* Claimed profiles */}
        <Section
          title="My Profiles"
          subtitle="Profiles you've claimed after completing the combine."
        >
          {user.claimedProfiles?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {user.claimedProfiles.map(id => (
                <ClaimedProfileCard key={id} shareableId={id} />
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center', padding: '28px 16px',
              border: '1px dashed var(--border)', borderRadius: 10,
            }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 14 }}>
                You haven't claimed any profiles yet.
              </p>
              <Link to="/" className="btn btn--primary" style={{ fontSize: 13, padding: '8px 24px' }}>
                Take the Combine →
              </Link>
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <Link to="/" className="btn btn--ghost" style={{ fontSize: 13, padding: '7px 18px' }}>
              + Take Another Combine
            </Link>
          </div>
        </Section>

        {/* Coach Twin — 30-day program (shows only if enrolled) */}
        <CoachTwinSection user={user} />

        {/* Notifications */}
        {user.notifications?.length > 0 && (
          <Section
            title="Notifications"
            subtitle="Activity on your claimed profiles."
          >
            <div>
              {[...user.notifications].reverse().slice(0, 20).map((n) => (
                <NotificationItem key={n.id} notification={n} />
              ))}
            </div>
          </Section>
        )}

        {/* Bookmarks */}
        {user.bookmarks?.length > 0 && (
          <Section title="Shortlist" subtitle="Profiles you've bookmarked.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {user.bookmarks.slice(0, 3).map((id) => (
                <ClaimedProfileCard key={id} shareableId={id} />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {user.bookmarks.length} bookmarked profile{user.bookmarks.length !== 1 ? 's' : ''}
              </span>
              {user.bookmarks.length > 3 && (
                <Link to="/shortlist" className="btn btn--ghost" style={{ fontSize: 13, padding: '7px 18px' }}>
                  View All {user.bookmarks.length} →
                </Link>
              )}
              {user.bookmarks.length <= 3 && (
                <Link to="/shortlist" className="btn btn--ghost" style={{ fontSize: 13, padding: '7px 18px' }}>
                  Manage Shortlist →
                </Link>
              )}
            </div>
          </Section>
        )}

        {/* Subscription */}
        <SubscriptionSection user={user} />

        {/* Profile settings */}
        <Section
          title="Scout Profile"
          subtitle="This information appears on your public profile and in scout searches."
        >
          <SettingsForm user={user} refreshUser={refreshUser} />
        </Section>

        {/* Profile Theme */}
        <Section
          title="Profile Theme"
          subtitle="Customize your profile's accent color and card style."
        >
          <ThemeSection user={user} refreshUser={refreshUser} />
        </Section>

        {/* Invite & Referrals */}
        <InviteSection />

        {/* Linked platforms */}
        <Section
          title="Linked Platforms"
          subtitle="Connect your gaming accounts to display them on your profile."
        >
          <LinkedPlatforms />
        </Section>

        {/* Delete account */}
        <DeleteAccountSection />

      </div>
    </div>
  );
}

function InviteSection() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getReferralInfo().then(setInfo).catch(() => {});
  }, []);

  useEffect(() => {
    const pending = localStorage.getItem('nr_ref');
    if (pending && user) {
      localStorage.removeItem('nr_ref');
      applyReferral(pending).catch(() => {});
    }
  }, [user]);

  const handleCopy = () => {
    if (!info?.referralLink) return;
    navigator.clipboard.writeText(info.referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Section
      title="Invite Friends"
      subtitle="Earn 1 free re-test credit for every player who signs up with your link."
    >
      {info ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              readOnly
              value={info.referralLink}
              className="input"
              style={{ flex: 1, fontSize: 13, fontFamily: 'var(--font-mono)' }}
              onFocus={(e) => e.target.select()}
            />
            <button className="btn btn--primary" style={{ whiteSpace: 'nowrap', padding: '0 18px' }} onClick={handleCopy}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            {info.referralCount > 0
              ? `You've referred ${info.referralCount} player${info.referralCount !== 1 ? 's' : ''} — ${info.referralCount} free re-test credit${info.referralCount !== 1 ? 's' : ''} earned.`
              : 'No referrals yet. Share your link to earn free re-test credits.'}
          </p>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Loading…</p>
      )}
    </Section>
  );
}

function DeleteAccountSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleDelete() {
    setError('');
    setLoading(true);
    try {
      await deleteAccount(password);
      logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{ marginBottom: 20, borderColor: 'var(--danger)' }}
    >
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--danger)', margin: 0 }}>Danger Zone</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
      </div>
      {!open ? (
        <button
          className="btn"
          style={{ background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: 13, padding: '8px 18px' }}
          onClick={() => setOpen(true)}
        >
          Delete Account
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0 }}>
            Enter your password to confirm deletion.
          </p>
          <input
            type="password"
            className="input"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ maxWidth: 300 }}
          />
          {error && <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn"
              style={{ background: 'var(--danger)', color: '#fff', fontSize: 13, padding: '8px 18px' }}
              onClick={handleDelete}
              disabled={loading || !password}
            >
              {loading ? 'Deleting…' : 'Confirm Delete'}
            </button>
            <button
              className="btn btn--ghost"
              style={{ fontSize: 13, padding: '8px 18px' }}
              onClick={() => { setOpen(false); setPassword(''); setError(''); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
